import type { AriPushRequest } from '@/shared/api';

export function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

export function plusDaysIso(days: number): string {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * Beim Ändern eines Datums das andere nachziehen, damit nie eine rückwärts laufende
 * Range (To < From) entsteht. Ändert der User "Von", springt "Bis" mit, solange "Bis"
 * leer ist oder vor dem neuen "Von" läge — und umgekehrt. Reine UX-Hilfe.
 */
export function snapDateRange(
  from: string,
  to: string,
  changed: 'from' | 'to',
): { from: string; to: string } {
  if (changed === 'from') {
    return { from, to: !to || to < from ? from : to };
  }
  return { from: !from || from > to ? to : from, to };
}

export type TriState = 'unchanged' | 'on' | 'off';

export interface AriFormState {
  from: string;
  to: string;
  rate: string;
  available: TriState;
  minStay: string;
  maxStay: string;
  closedToArrival: TriState;
  closedToDeparture: TriState;
  stopSell: TriState;
}

export function emptyAriForm(): AriFormState {
  return {
    from: todayIso(),
    to: plusDaysIso(13),
    rate: '',
    available: 'unchanged',
    minStay: '',
    maxStay: '',
    closedToArrival: 'unchanged',
    closedToDeparture: 'unchanged',
    stopSell: 'unchanged',
  };
}

export interface AriInitialValues {
  from?: string;
  to?: string;
  rate?: number | null;
  available?: boolean | null;
  minStay?: number | null;
  maxStay?: number | null;
  closedToArrival?: boolean | null;
  closedToDeparture?: boolean | null;
  stopSell?: boolean | null;
  /** Gespeicherte An-/Abreisesperre zum Anzeigen; sie wird nicht vorbelegt und nicht mitgesendet. */
  currentRestrictions?: { closedToArrival: boolean | null; closedToDeparture: boolean | null };
}

function boolToTri(b: boolean | null | undefined): TriState {
  if (b === true) return 'on';
  if (b === false) return 'off';
  return 'unchanged';
}

function numberOrEmpty(n: number | null | undefined): string {
  return n != null ? String(n) : '';
}

/**
 * Ein Mindestaufenthalt von 0 ist ungueltig; MaxStay=0 ist dagegen die ausdrueckliche
 * Channex-Semantik fuer "unbegrenzt" und muss im Formular sichtbar bleiben.
 */
function minimumStayNumberOrEmpty(n: number | null | undefined): string {
  return n != null && n > 0 ? String(n) : '';
}

function maximumStayNumberOrEmpty(n: number | null | undefined): string {
  return n != null && n >= 0 ? String(n) : '';
}

export function buildInitialForm(values: AriInitialValues): AriFormState {
  const base = emptyAriForm();
  return {
    ...base,
    from: values.from ?? base.from,
    to: values.to ?? values.from ?? base.to,
    rate: numberOrEmpty(values.rate),
    // Availability bewusst NICHT aktiv prefillen: sie ist ein eigener Channex-Call. Wuerde der
    // Kalender-Klick "Verfuegbar" vorbelegen, ginge bei einer reinen Preisaenderung ungewollt
    // ein zweiter (availability-)Call raus. Default "unchanged"; der aktuelle Status wird
    // read-only angezeigt (currentAvailable), der User schaltet nur bei Bedarf aktiv um.
    available: 'unchanged',
    minStay: minimumStayNumberOrEmpty(values.minStay),
    maxStay: maximumStayNumberOrEmpty(values.maxStay),
    closedToArrival: boolToTri(values.closedToArrival),
    closedToDeparture: boolToTri(values.closedToDeparture),
    stopSell: boolToTri(values.stopSell),
  };
}

function triStateToBool(t: TriState): boolean | null {
  if (t === 'unchanged') return null;
  if (t === 'on') return true;
  return false;
}

function numberOrNull(s: string): number | null {
  const trimmed = s.trim();
  if (trimmed.length === 0) return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/**
 * Der Client normalisiert nur leere, negative und gebrochene Eingaben. Die fachliche
 * Obergrenze und der effektive Min-/Max-Konflikt bleiben ausschliesslich serverseitig.
 */
export function stayNumberOrNull(s: string): number | null {
  const n = numberOrNull(s);
  if (n == null || !Number.isInteger(n) || n < 1) return null;
  return n;
}

export function maxStayNumberOrNull(s: string): number | null {
  const n = numberOrNull(s);
  if (n == null || !Number.isInteger(n) || n < 0) return null;
  return n;
}

export function buildAriPushRequest(propertyId: string, form: AriFormState): AriPushRequest {
  return {
    propertyId,
    from: form.from,
    to: form.to,
    rate: numberOrNull(form.rate),
    available: triStateToBool(form.available),
    minStay: stayNumberOrNull(form.minStay),
    maxStay: maxStayNumberOrNull(form.maxStay),
    closedToArrival: triStateToBool(form.closedToArrival),
    closedToDeparture: triStateToBool(form.closedToDeparture),
    stopSell: triStateToBool(form.stopSell),
  };
}

export function hasAnythingToPush(form: AriFormState): boolean {
  const r = buildAriPushRequest('', form);
  return (
    r.rate !== null ||
    r.available !== null ||
    r.minStay !== null ||
    r.maxStay !== null ||
    r.closedToArrival !== null ||
    r.closedToDeparture !== null ||
    r.stopSell !== null
  );
}
